import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const REASON_LABEL = {
  missing_recipient: "No billing email",
  invalid_recipient: "Invalid email address",
  no_authorized_networks: "No authorized networks",
  prices_not_available_for_date: "Prices not uploaded for the report date",
  needs_review: "Needs review (outcome unknown)",
  sending: "Send in progress",
  internal_test_account: "Internal test account",
};

const Stat = ({ label, value, testId }) => (
  <div className="border border-zinc-200 rounded-md px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-lg font-semibold" data-testid={testId}>{value}</div>
  </div>
);

function SkippedList({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="max-h-40 overflow-auto border border-zinc-200 rounded-md text-xs" data-testid="bulk-price-skipped-list">
      {rows.map((r) => (
        <div key={r.customer_id} className="flex justify-between gap-2 px-3 py-1.5 border-b border-zinc-100">
          <span className="truncate">{r.company_name || r.customer_id}</span>
          <span className="text-amber-700 text-right">
            {REASON_LABEL[r.reason.split(":")[0]] || r.reason}
            {r.reason.includes(":") && ` (${r.reason.split(":")[1]})`}
          </span>
        </div>
      ))}
    </div>
  );
}

function RecipientSummary({ prev }) {
  const bad = prev.invalid_recipients || [];
  return (
    <div className="text-xs space-y-1" data-testid="bulk-recipient-summary">
      <div>Eligible customers: <b data-testid="bulk-eligible-customers">{prev.eligible.length}</b> · Emails to send: <b data-testid="bulk-recipient-count">{prev.recipient_count ?? prev.eligible.length}</b> (one per address) · Addresses already sent: <b data-testid="bulk-already-sent-recipients">{prev.already_sent_recipient_count || 0}</b></div>
      {bad.length > 0 && (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded-md px-2 py-1.5" data-testid="bulk-invalid-recipients">
          Fix these email addresses (customer is not sent until corrected):
          {bad.map((b) => <div key={b.customer_id} className="font-mono">{b.company_name} · {b.invalid_addresses.join(", ")}</div>)}
        </div>
      )}
    </div>
  );
}

function ReviewRow({ cid, name, eff, r, onDone }) {
  const tid = `${cid}-${(r.to || "").replace(/[^a-z0-9]/gi, "_")}`;
  const [open, setOpen] = useState(null);
  const [ref, setRef] = useState(r.message_id || "");
  const [reason, setReason] = useState("");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/price-report-bulk/review/resolve", {
        customer_id: cid, effective_date: eff, recipient: r.to, action: open, verified_in_mailgun: true,
        provider_reference: ref || null, reason: reason || null,
      });
      toast.success(open === "confirm_sent" ? "Recorded as sent. Nothing was resent." : "Released. Nothing was sent; it can now be included in a new confirmed send or retry.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-3 py-2 border-b border-orange-100 space-y-1.5" data-testid={`bulk-review-row-${tid}`}>
      <div className="flex justify-between gap-2"><span>{name ? `${name} · ` : ""}<span className="font-mono">{cid}</span> · <span data-testid={`bulk-review-recipient-${tid}`}>{r.to}</span></span><span className="text-orange-700">{r.error || "Outcome unknown"}</span></div>
      {!open && (
        <div className="flex gap-2">
          <button className="gf-btn gf-btn-outline text-xs" onClick={() => setOpen("confirm_sent")} data-testid={`bulk-review-confirm-${tid}`}>Confirm it was sent</button>
          <button className="gf-btn gf-btn-outline text-xs" onClick={() => setOpen("allow_resend")} data-testid={`bulk-review-release-${tid}`}>Allow resend</button>
        </div>
      )}
      {open && (
        <div className="space-y-1.5">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid={`bulk-review-verified-${tid}`} />
            <span>{open === "confirm_sent" ? "I checked Mailgun and it accepted this email." : "I checked Mailgun and it did NOT accept this email."}</span>
          </label>
          <input className="gf-input w-full text-xs" placeholder={open === "confirm_sent" ? "Mailgun message ID (required)" : "Mailgun reference (optional)"} value={ref} onChange={(e) => setRef(e.target.value)} data-testid={`bulk-review-ref-${tid}`} />
          <input className="gf-input w-full text-xs" placeholder="Note (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`bulk-review-reason-${tid}`} />
          <div className="flex gap-2">
            <button className="gf-btn gf-btn-primary text-xs" disabled={busy || !checked || (open === "confirm_sent" && !ref.trim())} onClick={submit} data-testid={`bulk-review-submit-${tid}`}>Save</button>
            <button className="gf-btn gf-btn-outline text-xs" onClick={() => setOpen(null)} data-testid={`bulk-review-cancel-${tid}`}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Results({ camp, onRetry, busy, onRefresh }) {
  const c = camp.counts || {};
  const entries = Object.entries(camp.results || {});
  const failed = entries.filter(([, r]) => ["failed", "skipped", "released"].includes(r.status));
  const review = entries.flatMap(([cid, r]) => (r.recipients || []).filter((x) => x.status === "needs_review").map((x) => [cid, x]));
  return (
    <div className="space-y-3" data-testid="bulk-price-results">
      <div className="text-xs text-zinc-500">Campaign <span className="font-mono" data-testid="bulk-price-campaign-id">{camp.campaign_id}</span> · {camp.running ? "sending…" : camp.status}</div>
      <div className="grid grid-cols-5 gap-2">
        <Stat label="Eligible" value={camp.eligible?.length ?? 0} testId="bulk-res-eligible" />
        <Stat label="Submitted" value={c.submitted || 0} testId="bulk-res-submitted" />
        <Stat label="Failed" value={c.failed || 0} testId="bulk-res-failed" />
        <Stat label="Needs review" value={c.needs_review || 0} testId="bulk-res-needs-review" />
        <Stat label="Pending" value={c.pending || 0} testId="bulk-res-pending" />
      </div>
      {review.length > 0 && (
        <div className="text-xs border border-orange-200 rounded-md max-h-56 overflow-auto" data-testid="bulk-review-list">
          <div className="px-3 py-1.5 bg-orange-50 text-orange-800">These were handed to Mailgun but the result is unknown. Check Mailgun before choosing. They are never retried automatically.</div>
          {review.map(([cid, r]) => <ReviewRow key={`${cid}-${r.to}`} cid={cid} eff={camp.effective_date} r={r} onDone={onRefresh} />)}
        </div>
      )}
      {failed.length > 0 && (
        <div className="text-xs border border-red-200 rounded-md max-h-32 overflow-auto">
          {failed.map(([cid, r]) => (
            <div key={cid} className="px-3 py-1 border-b border-red-100 flex justify-between">
              <span className="font-mono">{cid}</span><span className="text-red-700">{r.status}: {r.error || r.reason}</span>
            </div>
          ))}
        </div>
      )}
      {((c.failed || 0) + (c.released || 0) + (c.pending || 0) + entries.filter(([, r]) => r.status === "skipped" && String(r.reason || "").startsWith("prices_not_available")).length) > 0 && !camp.running && (
        <button className="gf-btn gf-btn-outline" onClick={onRetry} disabled={busy} data-testid="bulk-price-retry-failed">Retry failed only</button>
      )}
    </div>
  );
}

function LaneDates({ prev, busy, onLatest }) {
  const lanes = prev.lane_status || [];
  const missing = lanes.filter((l) => !l.available);
  if (!lanes.length) return null;
  return (
    <div className={`text-xs border rounded-md ${missing.length ? "border-amber-300 bg-amber-50" : "border-zinc-200"}`} data-testid="bulk-lane-dates">
      <table className="w-full">
        <thead><tr className="text-zinc-500"><th className="text-left px-2 py-1">Network</th><th className="text-left">Expected</th><th className="text-left">Latest available</th><th className="text-left">Using</th><th className="text-right px-2">Customers</th></tr></thead>
        <tbody>
          {lanes.map((l) => (
            <tr key={`${l.network}:${l.country}`} data-testid={`bulk-lane-${l.network}-${l.country}`}>
              <td className="px-2 py-1">{l.network === "petro_pass" ? "Petro-Pass" : l.network === "esso" ? "Esso" : "Pipeline"} {l.country}</td>
              <td>{l.expected_date}</td>
              <td>{l.latest_available_date || "none"}{l.days_older ? <span className="text-amber-800"> ({l.days_older} day{l.days_older > 1 ? "s" : ""} older)</span> : null}</td>
              <td className={l.using_date ? (l.fallback ? "text-amber-800 font-semibold" : "") : "text-red-700"}>{l.using_date || "not sent"}</td>
              <td className="text-right px-2">{l.affected_customers}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {missing.length > 0 && (
        <label className="flex items-start gap-2 px-2 py-2 border-t border-amber-200 text-amber-900">
          <input type="checkbox" checked={!!prev.use_latest_available} disabled={busy} onChange={(e) => onLatest(e.target.checked)} data-testid="bulk-use-latest-available" />
          <span>Continue using latest available prices. Each report keeps its real effective date, so different networks may show different dates. Unticked (default): customers on these networks are not sent anything.</span>
        </label>
      )}
    </div>
  );
}

export default function BulkPriceReportModal({ onClose }) {
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  const [camp, setCamp] = useState(null);

  const load = async (useLatest = false) => {
    setBusy(true);
    try { const { data } = await api.post("/admin/price-report-bulk/preview", { use_latest_available: useLatest }); setPrev(data); setCamp(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare the summary"); }
    finally { setBusy(false); }
  };
  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const poll = async (id) => {
    const { data } = await api.get(`/admin/price-report-bulk/${id}`);
    setCamp(data);
    if (data.running || data.status === "sending") setTimeout(() => poll(id), 1500);
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/price-report-bulk/${prev.campaign_id}/confirm`, { snapshot_hash: prev.snapshot_hash });
      setCamp(data); poll(data.campaign_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
      if (e?.response?.status === 409) load();
    } finally { setBusy(false); }
  };
  const retry = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/price-report-bulk/${camp.campaign_id}/retry-failed`); setCamp(data); poll(data.campaign_id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="bulk-price-modal">
      <div className="bg-white rounded-lg w-full max-w-xl shadow-xl">
        <div className="p-4 border-b border-zinc-200">
          <div className="font-semibold">Send Price List to All Customers</div>
          <div className="text-xs text-zinc-500">Each eligible customer receives only their own price report. Nothing is sent until you confirm.</div>
        </div>
        <div className="p-4 space-y-3">
          {!prev && <div className="text-sm text-zinc-500" data-testid="bulk-price-loading">Preparing summary…</div>}
          {prev && !camp && (
            <>
              <div className="text-sm">Campaign date: <b data-testid="bulk-price-effective-date">{prev.effective_date}</b> <span className="text-xs text-zinc-500">(Canada: tomorrow · Petro-Pass USA: today, Eastern Time)</span></div>
              <LaneDates prev={prev} busy={busy} onLatest={(v) => load(v)} />
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Active customers" value={prev.active_customers} testId="bulk-price-active" />
                <Stat label="Opted in" value={prev.opted_in} testId="bulk-price-opted-in" />
                <Stat label="Eligible to send" value={prev.eligible.length} testId="bulk-price-eligible" />
                <Stat label="Skipped" value={prev.skipped.length} testId="bulk-price-skipped" />
                <Stat label="Already sent" value={prev.already_sent.length} testId="bulk-price-already-sent" />
              </div>
              <SkippedList rows={prev.skipped} />
              {(prev.needs_review || []).length > 0 && (
                <div className="text-xs border border-orange-200 rounded-md max-h-56 overflow-auto" data-testid="bulk-preview-review-list">
                  <div className="px-3 py-1.5 bg-orange-50 text-orange-800">These were handed to Mailgun but the result is unknown. Check Mailgun before choosing. They are never resent automatically.</div>
                  {prev.needs_review.map((r) => (
                    <ReviewRow key={`${r.customer_id}-${r.recipient}`} cid={r.customer_id} name={r.company_name} eff={prev.effective_date} r={{ to: r.recipient }} onDone={() => load(!!prev.use_latest_available)} />
                  ))}
                </div>
              )}
              <RecipientSummary prev={prev} />
              {prev.transport === "mock" && <div className="text-xs text-sky-700" data-testid="bulk-price-test-sink">Preview test sink: no real emails will be delivered.</div>}
              {prev.transport === "disabled" && <div className="text-xs text-red-700" data-testid="bulk-price-disabled">Bulk sending is not enabled on this environment.</div>}
            </>
          )}
          {camp && <Results camp={camp} onRetry={retry} busy={busy} onRefresh={() => poll(camp.campaign_id)} />}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button className="gf-btn gf-btn-outline" onClick={onClose} disabled={busy} data-testid="bulk-price-cancel">{camp ? "Close" : "Cancel"}</button>
          {prev && !camp && (
            <button className="gf-btn gf-btn-primary" onClick={confirm}
              disabled={busy || !prev.eligible.length || prev.transport === "disabled"} data-testid="bulk-price-confirm">
              Confirm &amp; Send
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
